import { TenantContext } from '../models/types';
import { mockTenantContext } from '../mock/tenantContext';
import { handleAuthRedirect } from './authRedirect';

export interface ContextResponse extends TenantContext {
  entitlements?: string[];
}

/** In-flight promise cache with TTL to avoid duplicate concurrent requests. */
const CACHE_TTL_MS = 30_000;
let cachedEntry: { promise: Promise<ContextResponse>; expiresAt: number } | null = null;

export class ContextService {
  static async loadTenant(): Promise<ContextResponse> {
    const now = Date.now();
    if (cachedEntry && now < cachedEntry.expiresAt) {
      return cachedEntry.promise;
    }

    const promise = ContextService._fetch();
    cachedEntry = { promise, expiresAt: now + CACHE_TTL_MS };

    // On failure, evict the cache so the next call retries.
    promise.catch(() => {
      if (cachedEntry?.promise === promise) {
        cachedEntry = null;
      }
    });

    return promise;
  }

  /** Clear the in-flight cache (useful for tests or forced refresh). */
  static invalidate(): void {
    cachedEntry = null;
  }

  private static async _fetch(): Promise<ContextResponse> {
    try {
      const response = await fetch('/api/context');
      if (await handleAuthRedirect(response)) {
        throw new Error('AUTH_REDIRECT');
      }
      if (!response.ok) {
        throw new Error(`Context API returned ${response.status}`);
      }

      return (await response.json()) as ContextResponse;
    } catch (error) {
      const isLocalhost =
        typeof window !== 'undefined' &&
        ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname);
      if (isLocalhost) {
        console.warn('Using mock tenant context because context API is unavailable', error);
        return mockTenantContext;
      }
      throw error;
    }
  }
}
